import { ARTICLE_CARD_TYPE } from '@/constants/blog';
import { getOrganizationId } from '@/linkedData/index-lkd';
import { getBlogImageSrc } from '@/utils/blogUtils';
import { HtmlEntities } from '@/utils/HtmlEntities';
import { sanitizeUrl, withEndSlash } from '@/utils/millesimaUtils';

const SCHEMA = 'https://schema.org';
const BLOG_PATH = 'blog/';

// Same image in the three heights the blog serves
const IMAGE_TYPES = [ARTICLE_CARD_TYPE.SMALL, ARTICLE_CARD_TYPE.MEDIUM, ARTICLE_CARD_TYPE.LARGE];

/**
 * Canonical url of an article, built like the one of its canonical link.
 * @param {object} article The article
 * @param {object} storeConf The store configuration
 * @return {string|undefined} The url
 */
export const getArticleUrl = (article, storeConf) =>
	article?.urlKeyword ? sanitizeUrl(`${withEndSlash(storeConf?.url ?? '')}${BLOG_PATH}`, article.urlKeyword) : undefined;

/**
 * Display name of the author, whatever the fields the api filled in.
 * @param {object} author The article author
 * @return {string|undefined} The name
 */
export const getAuthorName = (author) =>
	(author?.fullName || [author?.firstName, author?.lastName].filter(Boolean).join(' ')).trim() || undefined;

// An article without an image must not advertise urls built on an undefined file name
const getImages = (imageName) => (imageName ? IMAGE_TYPES.map((type) => getBlogImageSrc(imageName, false, type)) : undefined);

/**
 * Build the structured data of a blog article.
 * @param {object} article The article
 * @param {object} storeConf The store configuration
 * @return {object|string} The BlogPosting node, or an empty string when the article cannot be described
 */
const buildLinkedData = (article, storeConf) => {
	const url = getArticleUrl(article, storeConf);

	if (!url) {
		return '';
	}

	// The Organization node is injected on every page, so referencing it by @id is enough
	const organizationId = getOrganizationId(storeConf);
	const authorName = getAuthorName(article.author);
	const description = HtmlEntities.decode(article.shortDesc || article.metaDescription || article.metaDesc);
	const images = getImages(article.imageName);

	return {
		'@context': SCHEMA,
		'@type': 'BlogPosting',
		mainEntityOfPage: { '@type': 'WebPage', '@id': url },
		url,
		headline: HtmlEntities.decode(article.title),
		...(description && { description }),
		...(images && { image: images }),
		...(article.publishedAt && { datePublished: article.publishedAt }),
		// updatedAt is the modification date; createdAt is unrelated to it
		...((article.updatedAt || article.publishedAt) && { dateModified: article.updatedAt || article.publishedAt }),
		author: authorName ? { '@type': 'Person', name: authorName } : { '@id': organizationId },
		publisher: { '@id': organizationId },
	};
};

export default buildLinkedData;
